"use client";

import { c as _c } from "react/compiler-runtime";
import Link from "next/link";
import { GraduationCap, ArrowRight } from "lucide-react";
import type { CampaignPageSection } from "@growwithai/shared";
import SafeImage from "@/components/common/SafeImage";
import { formatRupiah } from "@/lib/utils";
import type { LandingCourse } from "../types";
import { sectionBgStyle, sectionHeadingStyle } from "../section-style";
type FeaturedCoursesProps = Extract<CampaignPageSection, {
  type: "featured_courses";
}>;

// Tidak ada route publik /kelas/[slug]; kelas berbayar → /order/[slug]
// (checkout publik eksisting), kelas gratis → /register (akses setelah punya akun).
function courseHref(course: LandingCourse): string {
  if (course.access_type === "free") return "/register";
  return `/order/${course.slug}`;
}
const LEVEL_LABEL: Record<string, string> = {
  beginner: "Pemula",
  intermediate: "Menengah",
  advanced: "Lanjutan"
};

/** Grid kartu kelas — pola kartu mengikuti katalog kelas eksisting. */
export default function FeaturedCoursesSection(t0) {
  const $ = _c(30);
  const {
    section,
    courses
  } = t0;
  let t1;
  let t2;
  let t3;
  let t4;
  let t5;
  let t6;
  let t7;
  if ($[0] !== courses || $[1] !== section.category || $[2] !== section.custom_style || $[3] !== section.heading || $[4] !== section.limit) {
    t7 = Symbol.for("react.early_return_sentinel");
    bb0: {
      const filtered = (section.category ? courses.filter(c => (c.category ?? "").toLowerCase() === section.category.toLowerCase()) : courses).slice(0, section.limit);
      if (filtered.length === 0) {
        t7 = null;
        break bb0;
      }
      t5 = "bg-warm-cream py-14 md:py-20";
      if ($[12] !== section.custom_style) {
        t6 = sectionBgStyle(section.custom_style);
        $[12] = section.custom_style;
        $[13] = t6;
      } else {
        t6 = $[13];
      }
      t3 = "mx-auto max-w-6xl px-5";
      let t8;
      if ($[14] !== section.custom_style) {
        t8 = sectionHeadingStyle(section.custom_style);
        $[14] = section.custom_style;
        $[15] = t8;
      } else {
        t8 = $[15];
      }
      if ($[16] !== section.heading || $[17] !== t8) {
        t4 = <h2 className="text-2xl font-bold tracking-tight text-warm-black md:text-3xl" style={t8}>{section.heading}</h2>;
        $[16] = section.heading;
        $[17] = t8;
        $[18] = t4;
      } else {
        t4 = $[18];
      }
      t1 = "mt-8 grid grid-cols-1 gap-5 sm:grid-cols-2 lg:grid-cols-3";
      t2 = filtered.map(_temp);
    }
    $[0] = courses;
    $[1] = section.category;
    $[2] = section.custom_style;
    $[3] = section.heading;
    $[4] = section.limit;
    $[5] = t1;
    $[6] = t2;
    $[7] = t3;
    $[8] = t4;
    $[9] = t5;
    $[10] = t6;
    $[11] = t7;
  } else {
    t1 = $[5];
    t2 = $[6];
    t3 = $[7];
    t4 = $[8];
    t5 = $[9];
    t6 = $[10];
    t7 = $[11];
  }
  if (t7 !== Symbol.for("react.early_return_sentinel")) {
    return t7;
  }
  let t8;
  if ($[19] !== t1 || $[20] !== t2) {
    t8 = <div className={t1}>{t2}</div>;
    $[19] = t1;
    $[20] = t2;
    $[21] = t8;
  } else {
    t8 = $[21];
  }
  let t9;
  if ($[22] !== t3 || $[23] !== t4 || $[24] !== t8) {
    t9 = <div className={t3}>{t4}{t8}</div>;
    $[22] = t3;
    $[23] = t4;
    $[24] = t8;
    $[25] = t9;
  } else {
    t9 = $[25];
  }
  let t10;
  if ($[26] !== t5 || $[27] !== t6 || $[28] !== t9) {
    t10 = <section className={t5} style={t6}>{t9}</section>;
    $[26] = t5;
    $[27] = t6;
    $[28] = t9;
    $[29] = t10;
  } else {
    t10 = $[29];
  }
  return t10;
}
function _temp(course) {
  const isFree = course.access_type === "free";
  return <Link key={course.id} href={courseHref(course)} className="group flex flex-col overflow-hidden rounded-2xl border [border-color:var(--card-border-color,rgba(0,0,0,.06))] bg-white shadow-[var(--card-shadow,0_1px_2px_rgba(0,0,0,.05))] [backdrop-filter:var(--card-backdrop,none)] transition hover:-translate-y-0.5 hover:border-[var(--brand-primary)]/30 hover:shadow-md"><div className="relative aspect-video w-full overflow-hidden bg-[var(--brand-primary)]/10">{course.thumbnail_url ? <SafeImage src={course.thumbnail_url} alt={course.title} loading="lazy" className="h-full w-full object-cover transition-transform duration-300 group-hover:scale-[1.03]" /> : <div className="flex h-full w-full items-center justify-center text-[var(--brand-primary)]"><GraduationCap className="h-10 w-10 opacity-60" /></div>}<span className={`absolute left-3 top-3 rounded-full px-2.5 py-1 text-[10px] font-bold tracking-wide ${isFree ? "bg-green-100 text-green-700" : "bg-amber-100 text-amber-700"}`}>{isFree ? "GRATIS" : "PREMIUM"}</span></div><div className="flex flex-1 flex-col p-4"><h3 className="font-semibold leading-snug text-warm-black group-hover:text-[var(--brand-primary)]">{course.title}</h3>{course.subtitle && <p className="mt-1 line-clamp-2 text-sm text-gray-500">{course.subtitle}</p>}<div className="mt-auto flex items-center justify-between gap-2 pt-4"><span className="text-sm font-bold text-[var(--brand-primary)]">{isFree ? "Gratis" : typeof course.price === "number" && course.price > 0 ? formatRupiah(course.price) : "Premium"}</span><span className="inline-flex items-center gap-1 text-xs font-medium text-gray-500 transition-colors group-hover:text-[var(--brand-primary)]">{course.level ? LEVEL_LABEL[course.level] ?? course.level : "Lihat kelas"}<ArrowRight className="h-3.5 w-3.5" /></span></div></div></Link>;
}